import type {
  InputProofBytesType,
  KmsDelegatedUserDecryptEIP712Type,
  ZKProofLike,
} from "@zama-fhe/relayer-sdk/bundle";
import { toHex } from "viem";
import type { Address, Hex } from "viem";
import { IndexedDBStorage } from "../storage/indexeddb-storage";
import type { GenericStorage } from "../types";
import type { RelayerWorkerClient } from "../worker/worker.client";
import type { FheChain } from "../chains/types";
import type { TransportKeyPair } from "../credentials/types";
import { BaseRelayer } from "./base-relayer";
import { FheArtifactCache } from "./fhe-artifact-cache";
import type { RelayerSDK } from "./relayer-sdk";
import type {
  ClearValue,
  DelegatedUserDecryptParams,
  EIP712TypedData,
  EncryptParams,
  EncryptResult,
  EncryptedValue,
  FheEncryptionKey,
  PublicDecryptResult,
  PublicParamsData,
  RelayerWebConfig,
  UserDecryptParams,
} from "./relayer-sdk.types";
import { withRetry } from "./relayer-utils";

/**
 * Pinned relayer SDK version used for the WASM CDN bundle.
 * Update this when upgrading @zama-fhe/relayer-sdk, and keep the
 * peerDependencies range in package.json in sync (~x.y.z).
 */
export const RELAYER_SDK_VERSION = "0.4.4";
export const CDN_URL = `https://cdn.zama.org/relayer-sdk-js/${RELAYER_SDK_VERSION}/relayer-sdk-js.umd.cjs`;
/** SHA-384 hex digest of the pinned CDN bundle for integrity verification. */
export const CDN_INTEGRITY =
  "a50426aae8440e802102c8674dd8451f34fe79352d5e2cc6b89d9f1aa340296176c2ee33d9aa4657752f8ca96f74f921";

/**
 * RelayerWeb — single-chain browser encryption/decryption layer.
 * The worker is injected at construction time; the relayer does not own its lifecycle.
 */
export class RelayerWeb extends BaseRelayer implements RelayerSDK, Disposable {
  #artifactCache: FheArtifactCache | null = null;
  #artifactStorage: GenericStorage | null = null;
  readonly #config: RelayerWebConfig;

  constructor(config: RelayerWebConfig) {
    super();
    this.#config = config;
  }

  protected get chain(): FheChain {
    return this.#config.chain;
  }

  protected async init(): Promise<void> {
    await this.#worker.initWorker();
  }

  get #worker(): RelayerWorkerClient {
    return this.#config.worker;
  }

  #getArtifactCache(): FheArtifactCache {
    if (!this.#artifactCache) {
      if (!this.#artifactStorage) {
        this.#artifactStorage =
          this.#config.fheArtifactStorage ??
          new IndexedDBStorage("FheArtifactCache", 1, "artifacts");
      }
      this.#artifactCache = new FheArtifactCache({
        storage: this.#artifactStorage,
        chainId: this.chain.id,
        relayerUrl: this.chain.relayerUrl,
        ttl: this.#config.fheArtifactCacheTTL,
        logger: this.#config.logger,
      });
    }
    return this.#artifactCache;
  }

  /**
   * Terminate clears the artifact cache only.
   * The worker is externally owned — the relayer does not terminate it.
   */
  terminate(): void {
    this.#artifactCache = null;
    this.resetInit();
  }

  /** Calls {@link terminate}. */
  [Symbol.dispose](): void {
    this.terminate();
  }

  /**
   * Refresh the CSRF token in the worker.
   * Call this before making authenticated network requests.
   */
  async #refreshCsrfToken(): Promise<void> {
    const token = this.#config.security?.getCsrfToken?.() ?? "";
    if (token) {
      await this.#worker.updateCsrf(token);
    }
  }

  /**
   * Generate a transport key pair (ML-KEM public + private key) used for user-decryption.
   */
  async generateTransportKeyPair(): Promise<TransportKeyPair> {
    await this.ensureInit();
    const chainId = this.chain.id;
    const result = await this.#worker.generateKeypair({ chainId });
    return { publicKey: result.publicKey, privateKey: result.privateKey };
  }

  /**
   * Create EIP712 typed data for user decryption authorization.
   */
  async createEIP712(
    publicKey: Hex,
    contractAddresses: Address[],
    startTimestamp: number,
    durationDays = 7,
  ): Promise<EIP712TypedData> {
    await this.ensureInit();
    const chainId = this.chain.id;
    return this.#worker.createEIP712({
      chainId,
      publicKey,
      contractAddresses,
      startTimestamp,
      durationDays,
    });
  }

  /**
   * Encrypt values for use in smart contract calls.
   * Each value must specify its FHE type (ebool, euint8–256, eaddress).
   */
  async encrypt(params: EncryptParams): Promise<EncryptResult> {
    const { values, contractAddress, userAddress } = params;
    await this.ensureInit();
    const chainId = this.chain.id;

    return withRetry(async () => {
      await this.#refreshCsrfToken();
      const result = await this.#worker.encrypt({ chainId, values, contractAddress, userAddress });
      return {
        encryptedValues: result.handles.map((handle) => toHex(handle)),
        inputProof: toHex(result.inputProof),
      };
    });
  }

  /**
   * Decrypt ciphertexts using user's private key.
   * Requires a valid EIP712 signature.
   */
  async userDecrypt(
    params: UserDecryptParams,
  ): Promise<Readonly<Record<EncryptedValue, ClearValue>>> {
    await this.ensureInit();
    const chainId = this.chain.id;
    return withRetry(async () => {
      await this.#refreshCsrfToken();
      const result = await this.#worker.userDecrypt({ chainId, ...params });
      return result.clearValues;
    });
  }

  /**
   * Public decryption - no authorization needed.
   * Used for publicly visible encrypted values.
   */
  async publicDecrypt(encryptedValues: EncryptedValue[]): Promise<PublicDecryptResult> {
    await this.ensureInit();
    const chainId = this.chain.id;
    return withRetry(async () => {
      await this.#refreshCsrfToken();
      const result = await this.#worker.publicDecrypt({ chainId, encryptedValues });
      return {
        clearValues: result.clearValues,
        abiEncodedClearValues: result.abiEncodedClearValues,
        decryptionProof: result.decryptionProof,
      };
    });
  }

  /**
   * Create EIP712 typed data for delegated user decryption authorization.
   */
  async createDelegatedUserDecryptEIP712(
    publicKey: Hex,
    contractAddresses: Address[],
    delegatorAddress: Address,
    startTimestamp: number,
    durationDays = 7,
  ): Promise<KmsDelegatedUserDecryptEIP712Type> {
    await this.ensureInit();
    const chainId = this.chain.id;
    return this.#worker.createDelegatedUserDecryptEIP712({
      chainId,
      publicKey,
      contractAddresses,
      delegatorAddress,
      startTimestamp,
      durationDays,
    });
  }

  /**
   * Decrypt ciphertexts via delegation.
   * Requires a valid EIP712 signature from the delegator.
   */
  async delegatedUserDecrypt(
    params: DelegatedUserDecryptParams,
  ): Promise<Readonly<Record<EncryptedValue, ClearValue>>> {
    await this.ensureInit();
    const chainId = this.chain.id;
    return withRetry(async () => {
      await this.#refreshCsrfToken();
      const result = await this.#worker.delegatedUserDecrypt({ chainId, ...params });
      return result.clearValues;
    });
  }

  /**
   * Submit a ZK proof to the relayer for verification.
   */
  async requestZKProofVerification(zkProof: ZKProofLike): Promise<InputProofBytesType> {
    await this.ensureInit();
    const chainId = this.chain.id;
    return withRetry(async () => {
      await this.#refreshCsrfToken();
      return this.#worker.requestZKProofVerification({ chainId, zkProof });
    });
  }

  /**
   * Fetch the network's FHE encryption key (ID + bytes).
   * When storage is configured, the result is cached persistently.
   */
  async fetchFheEncryptionKeyBytes(): Promise<FheEncryptionKey | null> {
    await this.ensureInit();
    const chainId = this.chain.id;
    const artifactCache = this.#getArtifactCache();
    return artifactCache.fetchFheEncryptionKeyBytes(
      async () => (await this.#worker.getPublicKey({ chainId })).result,
    );
  }

  /**
   * Get public parameters for encryption capacity.
   * When storage is configured, the result is cached persistently.
   */
  async getPublicParams(bits: number): Promise<PublicParamsData | null> {
    await this.ensureInit();
    const chainId = this.chain.id;
    const artifactCache = this.#getArtifactCache();
    return artifactCache.getPublicParams(
      bits,
      async () => (await this.#worker.getPublicParams({ chainId, bits })).result,
    );
  }
}
